import { Component, type ErrorInfo, type ReactNode } from 'react';

import { BUILD_ID } from '../features/auth/buildInfo';

/**
 * 전역 렌더 오류 화면.
 *
 * 이 앱에는 루트에 에러 바운더리가 없었다. React에서 렌더 중 예외가 나면
 * 트리가 통째로 사라져 사용자는 흰 화면만 본다 — 어떤 화면이 왜 실패했는지
 * 알 수 있는 단서가 남지 않는다. 실제로 /chats 진입 시 흰 화면이 보고되었다.
 *
 * 예외를 화면에 드러내는 것이 목적이고, 복구는 부차적이다. 자동 복구는 하지
 * 않는다 — 상태를 모르는 채로 다시 그리면 같은 예외가 다른 지점에서 재현되어
 * 원인이 더 어려워진다.
 */

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  /** 유저가 stacks를 펼쳤는지 — 기본은 접어둔다 */
  showStack: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, showStack: false };

  static getDerivedStateFromError(error: Error): State {
    return { error, showStack: false };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 콘솔에도 남긴다 — 개발자 도구에서 스택과 컴포넌트 트리를 볼 수 있어야 한다
    console.error('[talk2nIcK] 렌더 오류:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleDismiss = (): void => {
    this.setState({ error: null, showStack: false });
  };

  override render(): ReactNode {
    const { error, showStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="app-shell flex flex-col items-center justify-center gap-4 px-8 text-center">
        <h1 className="text-lg font-semibold">문제가 발생했습니다</h1>
        <p className="text-sm text-[var(--text-secondary)]">
          아래 내용을 함께 알려주시면 확인하겠습니다.
        </p>

        <pre className="max-h-48 w-full max-w-sm overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-md)] bg-[var(--bg-list)] p-3 text-left text-xs text-[var(--danger)]">
          {error.message || String(error)}
        </pre>

        {showStack && (
          <pre className="max-h-48 w-full max-w-sm overflow-auto whitespace-pre-wrap break-words rounded-[var(--radius-md)] bg-[var(--bg-list)] p-3 text-left text-[10px] text-[var(--text-secondary)]">
            {error.stack ?? '스택 없음'}
          </pre>
        )}

        <div className="flex w-full max-w-xs gap-3">
          <button
            type="button"
            className="pressable flex-1 rounded-[var(--radius-md)] bg-[var(--accent)] px-4 py-3 font-medium text-white"
            onClick={this.handleReload}
          >
            새로고침
          </button>
          <button
            type="button"
            className="pressable flex-1 rounded-[var(--radius-md)] border border-[var(--divider)] px-4 py-3 font-medium"
            onClick={this.handleDismiss}
          >
            {showStack ? '숨기기' : '세부정보'}
          </button>
        </div>

        <p className="text-[10px] text-[var(--text-secondary)] opacity-60">build {BUILD_ID}</p>
      </div>
    );
  }
}
